import React, { useEffect, useState } from 'react';
import {
  Box, Button as MuiButton, Chip, Table, TableHead, TableBody, TableRow, TableCell, Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { adminAPI } from '../../api/services/userApi';
import { formatDate } from '../../utils/dateFormater';
import EditFirstPromoterCampaignModal from '../EditFirstPromoterCampaignModal/EditFirstPromoterCampaignModal';
import type { FirstPromoterCampaignType } from '../../types/firstPromoterCampaignTypes';
import type { CouponType } from '../../types/couponTypes';

const statusOf = (campaign: FirstPromoterCampaignType): { label: string; color: 'success' | 'info' | 'default' } => {
  if (campaign.isSubscriptionFree) return { label: 'Free', color: 'success' };
  if (campaign.stripePromoCode) return { label: `Coupon: ${campaign.stripePromoCode}`, color: 'info' };
  return { label: 'Unconfigured', color: 'default' };
};

const FirstPromoterCampaignsTableAreaAdmin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [campaigns, setCampaigns] = useState<FirstPromoterCampaignType[]>([]);
  const [editingCampaign, setEditingCampaign] = useState<FirstPromoterCampaignType | null>(null);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const res = await adminAPI.getFirstPromoterCampaigns();
      setCampaigns(res.data.data?.campaigns ?? []);
    } catch (err) {
      toast.error('Failed to load FirstPromoter campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const onSync = async () => {
    setIsSyncing(true);
    try {
      const res = await adminAPI.syncFirstPromoterCampaigns();
      setCampaigns(res.data.data?.campaigns ?? []);
      toast.success(
        `Synced: ${res.data.data?.created ?? 0} new, ${res.data.data?.updated ?? 0} updated`,
      );
    } catch (err) {
      toast.error('Failed to sync campaigns from FirstPromoter');
    } finally {
      setIsSyncing(false);
    }
  };

  const onSaveFree = async () => {
    if (!editingCampaign) return;
    try {
      await adminAPI.updateFirstPromoterCampaign(editingCampaign.campaignId, {
        isSubscriptionFree: true,
      });
      toast.success(`"${editingCampaign.name}" marked subscription-free`);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to update campaign');
    }
  };

  const onSaveReset = async () => {
    if (!editingCampaign) return;
    try {
      await adminAPI.updateFirstPromoterCampaign(editingCampaign.campaignId, { reset: true });
      toast.success(`"${editingCampaign.name}" reset to unconfigured`);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to update campaign');
    }
  };

  const onSaveExistingCoupon = async (coupon: CouponType) => {
    if (!editingCampaign) return;
    try {
      await adminAPI.updateFirstPromoterCampaign(editingCampaign.campaignId, {
        existingCoupon: {
          promotionCodeId: coupon.id,
          code: coupon.code,
        },
      });
      toast.success(`"${editingCampaign.name}" now uses coupon "${coupon.code}"`);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to attach coupon');
    }
  };

  return (
    <Box display="flex" flexDirection="column" rowGap="24px">
      <Box display="flex" justifyContent="flex-end">
        <MuiButton variant="contained" onClick={onSync} disabled={isSyncing}>
          {isSyncing ? 'Syncing...' : 'Sync Campaigns'}
        </MuiButton>
      </Box>

      {isLoading ? (
        <div className="styled-app__loader-mini" />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>FirstPromoter ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Synced</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => {
                const status = statusOf(campaign);
                return (
                  <TableRow key={campaign.campaignId}>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>{campaign.fprCampaignId}</TableCell>
                    <TableCell>
                      <Chip label={status.label} color={status.color} size="small" />
                    </TableCell>
                    <TableCell>
                      {campaign.lastSyncedAt ? formatDate(new Date(campaign.lastSyncedAt)) : '—'}
                    </TableCell>
                    <TableCell>
                      <MuiButton size="small" onClick={() => setEditingCampaign(campaign)}>
                        Edit
                      </MuiButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {campaigns.length === 0 && (
            <Typography sx={{ mt: 2 }}>
              No campaigns yet - click &quot;Sync Campaigns&quot; to fetch them from FirstPromoter.
            </Typography>
          )}
        </Box>
      )}

      {editingCampaign && (
        <div>
          <EditFirstPromoterCampaignModal
            campaign={editingCampaign}
            onCloseModal={() => setEditingCampaign(null)}
            onSaveFree={onSaveFree}
            onSaveReset={onSaveReset}
            onSaveExistingCoupon={onSaveExistingCoupon}
          />
        </div>
      )}
    </Box>
  );
};

export default FirstPromoterCampaignsTableAreaAdmin;
